import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import Layout from "@/components/Layout";
import { useScreenZoom } from "@/lib/useScreenZoom";

// Public pages
import HomePage from "@/pages/HomePage";
import PricingPage from "@/pages/PricingPage";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import PublicAuctionDetail from "@/pages/PublicAuctionDetail";
import PublicTeamPage from "@/pages/PublicTeamPage";
import PublicPlayerCard from "@/pages/PublicPlayerCard";
import PublicRegistrationForm from "@/pages/PublicRegistrationForm";
import { OverlayLED, OverlayLEDRouter, OverlayLEDRoyal, OverlayStream, OverlayAuctioneer, OverlayTeamOwner, OverlayTeamsBalance, OverlayTeamCycle } from "@/pages/Overlays";

// Authenticated pages
import AdminDashboard from "@/pages/AdminDashboard";
import TeamOwnerDashboard from "@/pages/TeamOwnerDashboard";
import AuctionManage from "@/pages/AuctionManage";
import LiveAuction from "@/pages/LiveAuction";
import SuperadminConsole from "@/pages/SuperadminConsole";

function Layouted({ children }) { return <Layout>{children}</Layout>; }

// Intercepts Ctrl +/- / 0 so zoom applies per-screen instead of per-domain.
function ScreenZoomManager() {
  useScreenZoom();
  return null;
}

function DashboardRouter() {
  const { user } = useAuth();
  if (user?.role === "team_owner") return <TeamOwnerDashboard />;
  if (user?.role === "superadmin") return <SuperadminConsole />;
  return <AdminDashboard />;
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <ScreenZoomManager />
        <Toaster theme="light" position="top-right" richColors />
        <Routes>
          {/* Auth (standalone) */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />

          {/* Public player registration form (per auction slug) — no auth */}
          <Route path="/register/:slug" element={<PublicRegistrationForm />} />

          {/* Overlays — no layout, no auth */}
          <Route path="/overlay/:auctionId/led" element={<OverlayLEDRouter />} />
          <Route path="/overlay/:auctionId/led/classic" element={<OverlayLED />} />
          <Route path="/overlay/:auctionId/led/royal" element={<OverlayLEDRoyal />} />
          <Route path="/overlay/:auctionId/stream" element={<OverlayStream />} />
          <Route path="/overlay/:auctionId/auctioneer" element={<OverlayAuctioneer />} />
          <Route path="/overlay/:auctionId/team-owner" element={<OverlayTeamOwner />} />
          <Route path="/overlay/:auctionId/teams-balance" element={<OverlayTeamsBalance />} />
          <Route path="/overlay/:auctionId/team-cycle" element={<OverlayTeamCycle />} />

          {/* Public with layout */}
          <Route path="/" element={<HomePage />} />
          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/auction/:auctionId" element={<Layouted><PublicAuctionDetail /></Layouted>} />
          {/* Iter32: standalone full-page team roster + player-card export views */}
          <Route path="/auction/:auctionId/team/:teamId" element={<PublicTeamPage />} />
          <Route path="/auction/:auctionId/player/:playerId/card" element={<PublicPlayerCard />} />

          {/* Authenticated */}
          <Route path="/dashboard" element={<ProtectedRoute><Layouted><DashboardRouter /></Layouted></ProtectedRoute>} />
          <Route path="/team" element={<ProtectedRoute roles={["team_owner"]}><Layouted><TeamOwnerDashboard /></Layouted></ProtectedRoute>} />
          <Route path="/superadmin" element={<ProtectedRoute roles={["superadmin"]}><Layouted><SuperadminConsole /></Layouted></ProtectedRoute>} />
          <Route path="/auction/:auctionId/manage" element={<ProtectedRoute><Layouted><AuctionManage /></Layouted></ProtectedRoute>} />
          <Route path="/auction/:auctionId/live" element={<ProtectedRoute><Layouted><LiveAuction /></Layouted></ProtectedRoute>} />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
